import { getSdk } from '@/composables/useSdk';
import type { AudioAd } from '@cc-consulting-nv/ccsdk';

/**
 * Service for fetching and rotating audio ads.
 * - Must be initialized after SDK is ready (call initialize() when music is enabled)
 * - Prefetches audio media for instant playback
 * - Rotates through ads to avoid repetition
 * - Refreshes cache every 5 minutes to pick up changes
 * - If no ads available, retries every 5 minutes
 */
class AdService {
  private cachedAds: AudioAd[] = [];
  private prefetchedUrls: Set<string> = new Set();
  private lastFetchTime = 0;
  private lastPlayedAdIndex = -1;
  private readonly cacheTimeMs = 5 * 60 * 1000; // 5 minutes cache
  private refreshTimer: ReturnType<typeof setInterval> | null = null;
  private isInitialized = false;

  /**
   * Initialize the ad service. Call this after SDK is ready
   * and only if music is enabled (site_type includes "music").
   *
   * This method is idempotent - calling it multiple times is safe.
   * The first API call is delayed by 30 seconds to avoid blocking initial page load.
   */
  initialize(): void {
    if (typeof window === 'undefined') return;
    if (this.isInitialized) return;

    this.isInitialized = true;

    // Delay first ad fetch by 30 seconds to not block initial page load
    setTimeout(() => {
      void this.refreshAds();

      // Start background refresh timer after first load
      this.startRefreshTimer();
    }, 30_000);
  }

  /**
   * Check if the service has been initialized
   */
  isReady(): boolean {
    return this.isInitialized;
  }

  /**
   * Start the background refresh timer to keep ads up to date
   */
  private startRefreshTimer(): void {
    if (typeof window === 'undefined') return;

    // Clear existing timer if any
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
    }

    // Refresh ads every 5 minutes
    this.refreshTimer = setInterval(() => {
      void this.refreshAds();
    }, this.cacheTimeMs);
  }

  /**
   * Prefetch audio media for instant playback
   * Uses fetch() to load the audio file into browser cache
   */
  private async prefetchMedia(ads: AudioAd[]): Promise<void> {
    const prefetchPromises = ads
      .filter((ad) => ad.mediaUrl && !this.prefetchedUrls.has(ad.mediaUrl))
      .map(async (ad) => {
        try {
          // Use no-cors to allow browser to cache opaque responses from external ad host
          await fetch(ad.mediaUrl, {
            method: 'GET',
            mode: 'no-cors',
            credentials: 'omit',
            cache: 'force-cache',
          });

          // With opaque responses we can't inspect success; assume cached if fetch resolves
          this.prefetchedUrls.add(ad.mediaUrl);
        } catch {
          // Failed to prefetch, will load on demand
        }
      });

    // Prefetch all ads in parallel, but don't block on failures
    await Promise.allSettled(prefetchPromises);
  }

  /**
   * Fetch all audio ads from the API and update the cache
   */
  private async refreshAds(): Promise<void> {
    try {
      const sdk = getSdk();
      if (!sdk) {
        return;
      }

      const ads = await sdk.getAudioAds();

      // Update cache
      this.cachedAds = ads;
      this.lastFetchTime = Date.now();

      if (ads.length > 0) {
        // Prefetch audio media for all ads
        void this.prefetchMedia(ads);

        // Clean up prefetched URLs that are no longer in the ad list
        const currentUrls = new Set(ads.map((ad) => ad.mediaUrl));
        for (const url of this.prefetchedUrls) {
          if (!currentUrls.has(url)) {
            this.prefetchedUrls.delete(url);
          }
        }
      }
    } catch {
      // Keep existing cache on error, will retry on next interval
    }
  }

  /**
   * Get the next audio ad for playback between songs.
   * Rotates through available ads to avoid repetition.
   * Fetches ads from API if cache is empty or expired.
   *
   * @returns AudioAd if available, null if no ads or service not initialized
   */
  async getAudioAd(): Promise<AudioAd | null> {
    // Don't fetch ads if service hasn't been initialized
    if (!this.isInitialized) {
      return null;
    }

    const now = Date.now();

    // Fetch ads if cache is empty or expired
    if (this.cachedAds.length === 0 || now - this.lastFetchTime >= this.cacheTimeMs) {
      await this.refreshAds();
    }

    // No ads available
    if (this.cachedAds.length === 0) {
      return null;
    }

    // Rotate to next ad (wrap around if at end)
    this.lastPlayedAdIndex = (this.lastPlayedAdIndex + 1) % this.cachedAds.length;

    const ad = this.cachedAds[this.lastPlayedAdIndex];

    return ad ?? null;
  }

  /**
   * Get all cached ads (for debugging or display purposes)
   */
  getAllAds(): AudioAd[] {
    return [...this.cachedAds];
  }

  /**
   * Get the number of ads currently cached
   */
  getAdCount(): number {
    return this.cachedAds.length;
  }

  /**
   * Check if ads have been loaded
   */
  hasAds(): boolean {
    return this.cachedAds.length > 0;
  }

  /**
   * Check if an ad's media has been prefetched
   */
  isMediaPrefetched(mediaUrl: string): boolean {
    return this.prefetchedUrls.has(mediaUrl);
  }

  /**
   * Mark an ad as failed (e.g., unsupported format like WAV) and get the next one.
   * This removes the failed ad from the cache so it won't be played again this session.
   *
   * @param failedAd The ad that failed to play
   * @returns The next available ad, or null if no more ads
   */
  getNextAdAfterFailure(failedAd: AudioAd): AudioAd | null {
    // Remove the failed ad from the cache
    const failedIndex = this.cachedAds.findIndex((ad) => ad.id === failedAd.id);
    if (failedIndex !== -1) {
      this.cachedAds.splice(failedIndex, 1);

      // Also remove from prefetched URLs
      if (failedAd.mediaUrl) {
        this.prefetchedUrls.delete(failedAd.mediaUrl);
      }

      // Adjust lastPlayedAdIndex if needed
      if (failedIndex <= this.lastPlayedAdIndex) {
        this.lastPlayedAdIndex = Math.max(-1, this.lastPlayedAdIndex - 1);
      }
    }

    // No more ads available
    if (this.cachedAds.length === 0) {
      return null;
    }

    // Get the next ad in rotation
    this.lastPlayedAdIndex = (this.lastPlayedAdIndex + 1) % this.cachedAds.length;
    const nextAd = this.cachedAds[this.lastPlayedAdIndex];

    return nextAd ?? null;
  }

  /**
   * Force refresh the ad cache immediately
   */
  async forceRefresh(): Promise<void> {
    if (!this.isInitialized) return;
    await this.refreshAds();
  }

  /**
   * Clear the cached ads and reset rotation
   */
  clearCache(): void {
    this.cachedAds = [];
    this.prefetchedUrls.clear();
    this.lastFetchTime = 0;
    this.lastPlayedAdIndex = -1;
  }

  /**
   * Clean up resources (call when service is no longer needed)
   */
  destroy(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
    this.clearCache();
    this.isInitialized = false;
  }
}

// Export singleton instance
export const adService = new AdService();
export type { AudioAd };
